'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import type { ComponentProps, ComponentType, JSX } from 'react';
import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/app-tabs';
import { useGiftNotificationsRealtime } from '@/hooks/gifts/use-gift-notifications';
import { checkGate } from '@/lib/analytics';
import { GiftsReceivedList } from './gifts-received-list';
import type { GiftsSentList as GiftsSentListType } from './gifts-sent-list';

// Defer the Sent tab + its WithdrawGiftModal chunk until the user actually
// clicks the Sent tab. The Received tab is the default landing surface, so
// only that list (and its dependencies) need to ship in the cold bundle.
const GiftsSentList: ComponentType<ComponentProps<typeof GiftsSentListType>> = dynamic(() => import('./gifts-sent-list').then(m => ({
  default: m.GiftsSentList
})), {
  ssr: false
});
export type GiftsTabValue = 'received' | 'sent';
export interface GiftsTabProps {
  /** Initial active tab. Defaults to received. */
  initial?: GiftsTabValue;
}

/**
 * Top-level gift inbox surface (US-7) — Received | Sent sub-tabs.
 * Used on the dedicated `/gifts` page. Portfolio uses {@link GiftsSummaryCard} only.
 */
export function GiftsTab(props) {
  const $ = _c(17);
  const t = useTranslations("gift.inbox");
  const [tab, setTab] = useState(props.initial ?? "received");
  useGiftNotificationsRealtime();
  if (!checkGate("gifting")) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = v => {
      if (v === "received" || v === "sent") {
        setTab(v);
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== t) {
    t1 = t("tabs.received");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <TabsTrigger value="received">{t1}</TabsTrigger>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("tabs.sent");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <TabsTrigger value="sent">{t3}</TabsTrigger>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = <TabsList className="grid w-full grid-cols-2">{t2}{t4}</TabsList>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <TabsContent value="received" className="pt-4"><GiftsReceivedList /></TabsContent>;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <TabsContent value="sent" className="pt-4"><GiftsSentList /></TabsContent>;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== tab) {
    t8 = <Tabs value={tab} onValueChange={t0}>{t5}{t6}{t7}</Tabs>;
    $[14] = t5;
    $[15] = tab;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}